import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { getData } from 'country-list';
import ErrorMessage from '@components-clean/core/error';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, MultiValueEvaluationCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    NamedItemType,
    RiskEvaluationColor,
    RiskStatusType,
    TimeFrequencyType,
} from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import {
    convertReviewFrequency,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import {
    convertRiskScoreColorToWeight,
    mapRiskReviewStatus,
    mapRiskStatus,
    RiskReviewStatusesForFilter,
    RiskStatuses,
} from '@utils/risk-helper';
import { RiskService } from '@utils/risk.service';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';
import { getNamedItemsByType, getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import {
    CompleteRiskFields_Risk_evaluationHistory_RiskEvaluation as CompleteRiskFields_evaluationHistory,
    CompleteRiskFields_Risk_observations_RiskObservation as CompleteRiskFields_observations,
    CompleteRiskFields_Risk_observations_RiskObservation_actions_RiskAction,
} from '@queries/generated/fragments.generated';
import { getCmControlsChartAssociatedRisks } from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROL_CHART_RISKS } from '@queries/risk-assessment';

type Props = {
    studyRef: string;
    panelData?: cm2Panel_OpraQueries;
    dataset: CM2Dataset;
};

export const CmControlsChartAssociatedRisks = ({ studyRef, panelData, dataset }: Props) => {
    const { t, ready } = useTranslation('ram', { keyPrefix: 'riskLog.popOut' });
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const [riskStatuses, setRiskStatuses] = useState<string[]>([]);
    const [riskReviewStatuses, setRiskReviewStatuses] = useState<string[]>([]);
    const [risks, setRisks] = useState<unknown[]>([]);
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/purity
    const statuses: { id: string; text: string }[] = Object.assign([], RiskService.riskStatusList);
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    // eslint-disable-next-line max-len
    const exportFileName = `${studyName}_Dataset ${stringDateWithoutTimeFromUtc(dataset.dataCutDate, 'DD-MMM-YYYY')}_${t('risksGrid.exportFileName')}_${panelData?.cm2Panel?.plotTitle}`;

    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    const { data: riskQualityIssueData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_QUALITY_ISSUE },
    });

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: studyRef,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });
    const { data: controlStrategyData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CONTROL_STRATEGY },
    });

    const { data: riskVendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });

    const { loading, error, data } = useQuery<getCmControlsChartAssociatedRisks>(GET_CM_CONTROL_CHART_RISKS, {
        variables: { studyId: studyRef, riskIds: panelData?.cm2Panel?.associatedRisksIds },
        fetchPolicy: 'no-cache',
    });

    const countries = getData().map((countryObject: { code: string; name: string }) => ({
        code: countryObject.code,
        name: countryObject.name,
    }));

    const evaluationDetails = (evaluationHistory: (CompleteRiskFields_evaluationHistory | null)[] | null) => {
        if (evaluationHistory && evaluationHistory.length > 0) {
            const mostRecentEval = evaluationHistory.reduce((newest, item) => {
                return new Date(item?.updated).getTime() > new Date(newest?.updated).getTime() ? item : newest;
            });
            return {
                impact: mostRecentEval?.impact,
                detectability: mostRecentEval?.detectability,
                likelihood: mostRecentEval?.likelihood,
                rationale: mostRecentEval?.rationale,
                ltd: (mostRecentEval?.likelihood ?? 0) * (mostRecentEval?.detectability ?? 0),
            };
        }
        return {
            ltd: 0,
        };
    };

    const openActionDetails = (observations: (CompleteRiskFields_observations | null)[]) => {
        if (observations && observations.length > 0) {
            const riskActions: CompleteRiskFields_Risk_observations_RiskObservation_actions_RiskAction[] = [];

            observations
                .filter((obs) => obs && !obs.closedDate && !obs.deleted)
                .forEach((ob: CompleteRiskFields_observations | null) => {
                    if (ob && ob.actions != null)
                        riskActions.push(
                            ...(ob.actions as CompleteRiskFields_Risk_observations_RiskObservation_actions_RiskAction[]),
                        );
                });

            return (
                riskActions &&
                riskActions.length &&
                riskActions.filter(
                    (riskAction: CompleteRiskFields_Risk_observations_RiskObservation_actions_RiskAction) =>
                        riskAction && !riskAction.completedDate && !riskAction.deleted,
                ).length
            );
        }
    };

    useEffect(() => {
        if (ready) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setRiskStatuses(RiskStatuses());
            setRiskReviewStatuses(RiskReviewStatusesForFilter());
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
    }, [ready]);

    useEffect(() => {
        if (!loadingResponsibleFunctions && data && riskVendorData && data.getCmControlsChartAssociatedRisks) {
            const array = data.getCmControlsChartAssociatedRisks
                .filter((r) => r !== null)
                .map((item) => {
                    const mappedStatus = item.status ? mapRiskStatus(item.status) : '';
                    return {
                        ...item,
                        createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                        updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
                        convertedOwner: item.owner
                            ? {
                                  name: `${item.owner.firstName} ${item.owner.lastName}`,
                                  active: item.owner.active,
                              }
                            : {
                                  name: '',
                                  active: true,
                              },
                        status: mappedStatus,
                        reviewRequired: item.reviewRequired,
                        ...evaluationDetails(item.evaluationHistory),
                        reviewFrequency: convertReviewFrequency(
                            item.reviewFrequencyPeriod ?? 0,
                            TimeFrequencyType[item.reviewFrequencyUnits ?? 'NONE'],
                        ),
                        nextReviewDate: item.nextReviewDate,
                        numActiveCtQs:
                            item.associatedVariables &&
                            item.associatedVariables.length &&
                            item.associatedVariables.filter((ctq) => ctq && ctq.active).length,
                        numOpenObservations:
                            (item.observations &&
                                item.observations.length &&
                                item.observations.filter((obs) => obs && !obs.closedDate && !obs.deleted).length) ??
                            0,
                        numDeletedObservations:
                            (item.observations &&
                                item.observations.length &&
                                item.observations.filter((obs) => obs && obs.deleted).length) ??
                            0,
                        numOpenActions: item.observations ? openActionDetails(item.observations) : 0,
                        countryList: item.countries?.map((a) => a?.name).join(', '),
                        vendorList: item.vendors?.map((a) => a?.name).join(', '),
                        canBeDeleted: item.status === statuses.find((s) => s.id === RiskStatusType.DRAFT)?.id,
                        reviewStatus: item.reviewStatus ? mapRiskReviewStatus(item.reviewStatus) : '',
                        riskRating: t(`riskRating.${item.score?.colour}`),
                        score: {
                            ...item.score,
                            movement:
                                item.score?.movement && item.score?.movement !== 'NO_MOVEMENT'
                                    ? t(`risksGrid.movement.${item.score?.movement}`)
                                    : '',
                            colour: item.score?.colour !== 'NONE' ? t(`riskRating.${item.score?.colour}`) : null,
                            prevColour:
                                item.score?.prevColour !== 'NONE' ? t(`riskRating.${item.score?.prevColour}`) : '',
                        },
                        created: stringDateWithoutTimeFromUtc(item.created),
                        updated: stringDateWithoutTimeFromUtc(item.updated),
                    };
                });
            // Sort by status
            let sortedResult = array.sort((a, b) => {
                if (a.status === b.status) return 0;
                if (
                    a.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                    a.status !== mapRiskStatus(RiskStatusType.CLOSED) &&
                    (b.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        b.status === mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return -1;

                return 0;
            });

            // Sort by Color
            sortedResult = sortedResult.sort((a, b) => {
                if (
                    a.score?.colour === b.score?.colour ||
                    ((a.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        a.status === mapRiskStatus(RiskStatusType.CLOSED)) &&
                        b.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                        b.status !== mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return 0;
                if (
                    a.score?.colour &&
                    b.score?.colour &&
                    convertRiskScoreColorToWeight(a.score?.colour as RiskEvaluationColor) >
                        convertRiskScoreColorToWeight(b.score?.colour as RiskEvaluationColor)
                )
                    return -1;

                if (a.score?.colour && !b.score?.colour) return -1;
                return 1;
            });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setRisks(sortedResult);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data, riskVendorData, loadingResponsibleFunctions]);

    const columns: GridColumnProps[] = [
        {
            id: 'statement',
            field: 'statement',
            title: t('risksGrid.columns.statement'),
            lockedByDefault: true,
            width: 250,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'riskScore',
            field: 'score',
            title: t('risksGrid.columns.riskScore'),
            customColumn: true,
            width: 250,
            minWidth: 180,
            ragColumn: true,
            evaluationColumn: true,
            filterKey: 'score',
            filter: 'numeric',
            cells: {
                data: (gridCellProps) =>
                    MultiValueEvaluationCell({
                        ...gridCellProps,
                        highlighted: true,
                        showBlankText: false,
                    }),
            },
            multiValueExportTitles: {
                prevScore: t('risksGrid.exportColumns.prevScore'),
                prevColour: t('risksGrid.exportColumns.prevColour'),
                score: t('risksGrid.exportColumns.score'),
                colour: t('risksGrid.exportColumns.colour'),
                movement: t('risksGrid.exportColumns.movement'),
            },
        },
        {
            id: 'riskEvaluation',
            field: 'aa',
            title: t('risksGrid.columns.riskEvaluation'),
            enhancedGroupedColumnToggling: true,
            sortable: false,
            children: [
                {
                    id: 'likelihood',
                    field: 'likelihood',
                    title: t('risksGrid.columns.likelihood'),
                    width: 80,
                    customColumn: true,
                    filter: 'numeric',
                },
                {
                    id: 'impact',
                    field: 'impact',
                    title: t('risksGrid.columns.impact'),
                    width: 80,
                    customColumn: true,
                    filter: 'numeric',
                },
                {
                    id: 'detectability',
                    field: 'detectability',
                    title: t('risksGrid.columns.detectability'),
                    width: 100,
                    customColumn: true,
                    filter: 'numeric',
                },
                {
                    id: 'rationale',
                    field: 'rationale',
                    title: t('risksGrid.columns.rationale'),
                    width: 300,
                    customColumn: true,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
            ],
        },
        {
            customColumn: true,
            minWidth: 100,
            id: 'id',
            field: 'recordId',
            title: 'ID',
        },
        {
            id: 'dateIdentified',
            field: 'dateIdentified',
            title: t('risksGrid.columns.dateIdentified'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'category',
            field: 'category',
            title: t('risksGrid.columns.category'),
            width: 200,
            minWidth: 200,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'status',
            field: 'status',
            title: t('risksGrid.columns.riskStatus'),
            customColumn: true,
            minWidth: 150,
            filter: 'text',
            lovData: riskStatuses,
        },
        {
            id: 'closedDate',
            field: 'closedDate',
            title: t('risksGrid.columns.closedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'reviewRequired',
            field: 'reviewRequired',
            title: t('risksGrid.columns.reviewRequired'),
            cells: { data: YesNoCell },
            customColumn: true,
            minWidth: 120,
            filter: 'boolean',
        },
        {
            id: 'reviewFrequency',
            field: 'reviewFrequency',
            title: t('risksGrid.columns.reviewFrequency'),
            customColumn: true,
            width: 100,
            minWidth: 150,
            filter: 'text',
        },
        {
            id: 'dateLastReviewed',
            field: 'dateLastReviewed',
            title: t('risksGrid.columns.lastReviewed'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'nextReviewDate',
            field: 'nextReviewDate',
            title: t('risksGrid.columns.nextReview'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'reviewStatus',
            field: 'reviewStatus',
            title: t('risksGrid.columns.reviewStatus'),
            customColumn: true,
            width: 100,
            minWidth: 150,
            filter: 'text',
            lovData: riskReviewStatuses,
        },
        {
            id: 'qualityIssue',
            field: 'qualityIssue',
            title: t('risksGrid.columns.qualityIssue'),
            width: 150,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskQualityIssueData?.namedItemsByType
                ? riskQualityIssueData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'qualityIssueReference',
            field: 'qualityIssueReference',
            title: t('risksGrid.columns.qualityIssueReference'),
            width: 200,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'owner',
            field: 'convertedOwner',
            title: t('risksGrid.columns.owner'),
            customColumn: true,
            width: 1,
            minWidth: 150,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('risksGrid.columns.responsibleFunction'),
            width: 180,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'controlStrategy',
            field: 'controlStrategy',
            title: t('risksGrid.columns.controlStrategy'),
            width: 180,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: controlStrategyData?.namedItemsByType
                ? controlStrategyData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'internal',
            field: 'internalOnly',
            title: t('risksGrid.columns.internal'),
            cells: { data: YesNoCell },
            customColumn: true,
            minWidth: 100,
            filter: 'boolean',
        },
        {
            id: 'vendors',
            field: 'vendorList',
            title: t('risksGrid.columns.vendors'),
            customColumn: true,
            minWidth: 200,
            filter: 'text',
            lovData: riskVendorData?.namedItemsByType
                ? riskVendorData.namedItemsByType.map((item) => item?.name as string)
                : [],
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    lovArrayField: 'vendors',
                }),
            },
        },
        {
            id: 'countries',
            field: 'countryList',
            title: t('risksGrid.columns.countries'),
            customColumn: true,
            minWidth: 200,
            filter: 'text',
            lovData: countries.map((c) => c.name),
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                    countryArrayField: 'countries',
                }),
            },
        },
        {
            id: 'numActiveControls',
            field: 'activeControlCount',
            title: t('risksGrid.columns.numActiveControls'),
            width: 120,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numActiveCtQs',
            field: 'numActiveCtQs',
            title: t('risksGrid.columns.numActiveCtQs'),
            width: 110,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numOpenObservations',
            field: 'numOpenObservations',
            title: t('risksGrid.columns.numOpenObservations'),
            width: 140,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'numOpenActions',
            field: 'numOpenActions',
            title: t('risksGrid.columns.numOpenActions'),
            width: 120,
            customColumn: true,
            filter: 'numeric',
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('risksGrid.columns.createdBy'),
            customColumn: true,
            minWidth: 150,
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'created',
            field: 'created',
            title: t('risksGrid.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('risksGrid.columns.updatedBy'),
            customColumn: true,
            minWidth: 150,
            showByDefault: false,
            alwaysExportColumn: true,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('risksGrid.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            customColumn: true,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
            alwaysExportColumn: true,
        },
    ];

    if (error) return <ErrorMessage />;

    if (!data || loadingResponsibleFunctions) return <Loading />;

    return (
        <Grid
            excludeHiddenRowMenuActions
            title={t('risksGrid.title')}
            maxHeight="calc(100% - 5px)"
            testDataId="popout-risks-grid"
            data={risks}
            columns={columns}
            loading={loading}
            useLoadingOverlay
            scrollable="virtual"
            addRecordTooltip={t('risksGrid.menuItems.addRisks')}
            dataItemKey="id"
            exportFilename={`${exportFileName}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
            service={AuditServiceType.CORE}
            area={AuditApplicationArea.RISK_ASSESSMENT}
            dataType={AuditDataType.EXCEL_EXPORT}
            showAddIcon={false}
            persistentStorageKey={String(PersistentStorageKeys['cm-controls-popout-risks-child'])}
        />
    );
};
